"use client";

import { ColorModeContext } from "@contexts/index";
import { Alert, Box, CircularProgress } from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { fileTypeFromBuffer } from "file-type";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import * as Sentry from "@sentry/react";
import { captureSentryExceptionOnce } from "src/hooks/sentry";
import { isImageReadable } from "src/utils/image-readable.util";
import { PHOTO_ACCEPT_TYPES } from "src/utils/upload-accept";
import {
  summariseRejections,
  type DropRejection,
} from "src/utils/upload-rejection";
import { readArrayBufferWithTimeout } from "./utils/read-with-timeout";
import { hitAndroidPickLimit } from "./utils/android-pick-limit";
import { forEachBatch } from "./utils/batches";
import {
  browserThumbnailEnv,
  decodeGate,
  lastResortThumbnail,
  renderThumbnail,
  THUMBNAIL_BREAKER_AFTER,
  thumbnailPatience,
  thumbnailWithTimeout,
} from "./utils/thumbnail";
import { detectCurrentInAppBrowser } from "src/utils/in-app-browser";
import pLimit from "p-limit";
type Heic2Any = (options: {
  blob: Blob;
  toType?: string;
  quality?: number;
}) => Promise<Blob | Blob[]>;

// Loaded the first time a HEIC preview is needed, not with the page: it is a
// 332 KB (gzipped) chunk, and fetching it at start-up competed with the
// landing page's own data on every visit, for a preview most visitors never
// open. A failed load is forgotten so the next preview can try again.
let heic2anyLoading: Promise<Heic2Any> | undefined;
function loadHeic2any(): Promise<Heic2Any> {
  heic2anyLoading ??= import("heic2any")
    .then((module) => module.default as Heic2Any)
    .catch((error) => {
      heic2anyLoading = undefined;
      throw error;
    });
  return heic2anyLoading;
}

// NOTE: there used to be an `uploadToDebuggingService()` here that POSTed the
// customer's ORIGINAL photo to https://file.io (a public, anonymous file-sharing
// host) whenever thumbnailing failed, then attached the resulting public link to
// a Sentry message. That shipped customer photos to a third party nobody
// consented to, on the most common failure path in the uploader (~1300 events in
// two weeks). Removed. Diagnosing a bad file needs its metadata, not its bytes —
// see the captureMessage in processBatch below, which now reports name/size/type
// only.
// isImageReadable now lives in its own module so it can be unit-tested without
// this component's heavy import graph. Re-exported so existing consumers of
// `@components/photo-upload` (e.g. photo-download) keep working.
export { isImageReadable };
// Create a text-based thumbnail for HEIC/HEIF files
export function createTextThumbnail(
  fileType: string,
  fileName: string
): string {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = 120;
  canvas.height = 120;

  if (ctx) {
    // Gradient background
    const gradient = ctx.createLinearGradient(0, 0, 120, 120);
    gradient.addColorStop(0, "#f8f9fa");
    gradient.addColorStop(1, "#e9ecef");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 120, 120);

    // Border
    ctx.strokeStyle = "#dee2e6";
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, 118, 118);

    // Icon circle background
    ctx.fillStyle = "#6c757d";
    ctx.beginPath();
    ctx.arc(60, 40, 18, 0, 2 * Math.PI);
    ctx.fill();

    // Camera icon (simplified)
    ctx.fillStyle = "white";
    ctx.font = "16px Arial";
    ctx.textAlign = "center";
    ctx.fillText("📷", 60, 46);

    // File type text
    ctx.fillStyle = "#495057";
    ctx.font = "bold 14px Arial";
    ctx.textAlign = "center";

    // Also the placeholder for slow JPEGs/PNGs once the thumbnail breaker
    // trips, so label it by the file, not always "HEIF".
    const fileExt =
      fileType === "image/heic"
        ? "HEIC"
        : fileType === "image/heif"
          ? "HEIF"
          : (/\.([a-z0-9]{1,5})$/i.exec(fileName)?.[1] ?? "FOTO").toUpperCase();
    ctx.fillText(fileExt, 60, 75);

    // "Click to view" text
    ctx.font = "10px Arial";
    ctx.fillStyle = "#6c757d";
    ctx.fillText("Click to view", 60, 90);

    // File name (truncated)
    ctx.font = "8px Arial";
    const truncatedName =
      fileName.length > 18 ? fileName.substring(0, 15) + "..." : fileName;
    ctx.fillText(truncatedName, 60, 105);
  }

  return canvas.toDataURL("image/png");
}

// heic2any's "this is not actually HEIC, the browser can already show it"
// rejection. It signals success-by-another-route, so it must not be treated as
// a conversion failure. Matched on the message because heic2any reuses code 1
// (ERR_USER) for several unrelated user-input rejections.
function isAlreadyBrowserReadable(error: unknown): boolean {
  const raw = error as { message?: unknown } | null;
  const message =
    error instanceof Error
      ? error.message
      : typeof raw?.message === "string"
        ? raw.message
        : "";
  return message.toLowerCase().includes("already browser readable");
}

// heic2any rejects with a plain object ({ code, message }), not an Error.
// Handing that to Sentry made it synthesise a garbage issue title (the issue
// literally showed up as "q") with no stack and no file name, so the failures
// were undiagnosable. Rebuild a real Error carrying the file name and the
// underlying code/message before reporting.
function toHeicConversionError(error: unknown, file: File): Error {
  const raw = error as { code?: unknown; message?: unknown } | null;
  const code = raw?.code !== undefined ? String(raw.code) : "unknown";
  const message =
    error instanceof Error
      ? error.message
      : typeof raw?.message === "string"
        ? raw.message
        : String(error);

  const wrapped = new Error(
    `HEIC/HEIF conversion failed for "${file.name}" (code ${code}): ${message}`
  );
  wrapped.name = "HeicConversionError";
  if (error instanceof Error) wrapped.stack = error.stack;
  return wrapped;
}

// Convert HEIC/HEIF to JPEG on demand.
// NOTE: this only powers the full-size PREVIEW in the photo modal. The upload
// itself always sends the original HEIC bytes, so a failure here costs the
// customer a preview, never the photo.
export async function convertHeicToJpeg(file: File): Promise<string> {
  const heic2any = await loadHeic2any();

  try {
    const convertedBlob = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.8,
    });

    return URL.createObjectURL(convertedBlob as Blob);
  } catch (error) {
    // Not every .heic file is actually HEIC. Messenger/WhatsApp hand back a
    // JPEG that kept the original name, and heic2any refuses it with ERR_USER
    // "Image is already browser readable". That is good news, not a failure:
    // the browser can render these bytes directly. Use them instead of
    // throwing (which cost the customer their preview) and reporting a
    // non-defect to Sentry (FOTO-NEXTJS-14D).
    if (isAlreadyBrowserReadable(error)) {
      return URL.createObjectURL(file);
    }

    const wrapped = toHeicConversionError(error, file);
    console.error(wrapped.message, error);
    captureSentryExceptionOnce(wrapped, {
      tags: { area: "photo-preview" },
      extra: { fileName: file.name, fileType: file.type, fileSize: file.size },
    });
    throw wrapped;
  }
}

export async function createThumbnail(
  file: File,
  type: string
): Promise<string> {
  // Never reject: a failed thumbnail must not crash an upload. Any unexpected
  // error falls back to a text thumbnail so the file still uploads.
  try {
    // HEIC/HEIF: a text thumbnail, as before. Most browsers cannot decode it,
    // and converting it here would be the most expensive thing on the page.
    if (type === "image/heic" || type === "image/heif") {
      return createTextThumbnail(type, file.name);
    }
    // Decoded at thumbnail size, never read whole into a data URL. This used
    // to run isImageReadable() first -- another full readAsDataURL and decode
    // per photo whose result was never used. See utils/thumbnail.ts.
    return await renderThumbnail(file, browserThumbnailEnv());
  } catch {
    return createTextThumbnail(file.type, file.name);
  }
}

/** Files handed to onUpload at a time: one state update per batch, not per file. */
const ADD_BATCH_SIZE = 30;

/**
 * Thumbnails decoded at once. Three keeps a phone responsive; two inside the
 * Facebook/Instagram WebView, which runs on the host app's memory budget.
 */
const thumbnailConcurrency = () => (detectCurrentInAppBrowser().isInApp ? 2 : 3);

const PhotoUpload = ({
  onUpload,
  setDisable,
}: {
  onUpload: (files: File[], thumbnails: string[]) => void | Promise<void>;
  setDisable: (value: boolean) => void;
}) => {
  const { mode } = useContext(ColorModeContext);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingLongerThan5Seconds, setIsUploadingLongerThan5Seconds] =
    useState(false);
  const timeoutIdRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const translate = useAppTranslate();
  // Shown after a pick that Android cut off at 100. Stays until a pick comes
  // back under the cap, i.e. until the customer has picked what they wanted.
  const [androidLimitHit, setAndroidLimitHit] = useState(false);
  // Names of the files the accept filter refused, shown inline (never an
  // alert); cleared by the next pick.
  const [rejectedCount, setRejectedCount] = useState(0);

  const processBatch = async (
    files: File[],
    batchSize: number,
    uploadCb: (
      thumbnails: (string | null)[],
      currentGlobalIndex: number
    ) => void | Promise<void>
  ): Promise<void> => {
    const concurrency = thumbnailConcurrency();
    const limit = pLimit(concurrency);
    // p-limit bounds the WAITING; this bounds the decoding, including renders
    // a timeout gave up on (see decodeGate).
    const decodes = decodeGate(concurrency * 2);
    const placeholderFor = (file: File) =>
      createTextThumbnail(file.type, file.name);

    // Timeouts in THIS pick; see thumbnailPatience for the circuit breaker.
    let thumbnailTimeouts = 0;
    const isTimeout = (error: unknown) =>
      error instanceof Error && error.message === "Processing timeout";

    const thumbnailFor = async (file: File) => {
      try {
        const isHeic = file.type === "image/heic" || file.type === "image/heif";
        const patience = thumbnailPatience(thumbnailTimeouts, isHeic);
        const render = decodes.run(() => createThumbnail(file, file.type));
        // Too many decodes still running (abandoned ones included): don't pile
        // another on. The photo is kept with a placeholder, as on a timeout.
        if (!render) return { file, thumbnail: placeholderFor(file) };
        try {
          const thumbnail = await thumbnailWithTimeout(
            render,
            patience.renderMs,
            new Error("Processing timeout")
          );
          return { file, thumbnail };
        } catch (error) {
          if (isTimeout(error)) thumbnailTimeouts += 1;
          // Breaker open: no 20 s head read and second render for this one.
          if (!patience.sniffFallback) throw error;
          console.warn(
            `Failed to process file, trying with the sniffed type: ${file.name}`,
            error
          );
          /*
           * Bounded: this read is awaited inside processBatch, which gates
           * the spinner and the disabled submit button. An iCloud-backed
           * photo whose bytes never arrive used to freeze the picker here
           * forever, silently. On timeout we fall through to the catch
           * below, which keeps the photo with a placeholder thumbnail.
           *
           * Only the head is read: file-type needs the first few KB, and
           * reading the whole file here doubled memory for a large photo.
           */
          const fileType = await fileTypeFromBuffer(
            await readArrayBufferWithTimeout(file.slice(0, 4100))
          );
          const sniffedRender = decodes.run(() =>
            createThumbnail(file, fileType?.mime ?? "image/jpeg"),
          );
          if (!sniffedRender) throw new Error("Decode gate full");
          const thumbnail = await thumbnailWithTimeout(
            sniffedRender,
            10000, // Reduced timeout for fallback
            new Error("Processing timeout")
          );
          return { file, thumbnail };
        }
      } catch (error) {
        // Both the timed-render attempt and the sniffed-type fallback
        // failed or timed out. The thumbnail is only a UI preview -- it
        // must never gate whether the photo itself gets uploaded, so we
        // still return the file (with a cheap object-URL placeholder)
        // instead of `null`, which used to silently drop it below.
        console.error(`Failed to process file: ${file.name}`, error);
        try {
          // Metadata only — never the file itself. Re-reading the whole
          // buffer just to sniff the mime also re-threw NotFoundError on a
          // stale File handle (iOS reclaims picked files), which turned a
          // recoverable thumbnail miss into a batch-level failure, so sniff
          // only a small head slice and tolerate it failing.
          let processedType: string | undefined;
          // With the breaker open this read would stall on the same iCloud
          // download as the thumbnail did; the report goes out without it.
          if (thumbnailTimeouts < THUMBNAIL_BREAKER_AFTER) {
            try {
              const head = await readArrayBufferWithTimeout(file.slice(0, 4100));
              processedType = (await fileTypeFromBuffer(head))?.mime;
            } catch {
              processedType = undefined;
            }
          }
          Sentry.captureMessage("Thumbnail generation failed", {
            level: "warning",
            extra: {
              fileDetails: {
                name: file.name,
                size: file.size,
                type: file.type,
                processedType,
              },
            },
          });
          console.error(
            `${translate("errors.corruptedFile")} ${file.name}: ${translate("errors.warning")}`
          );
        } catch (reportingError) {
          // Reporting itself must not be able to take the file down
          // with it.
          captureSentryExceptionOnce(reportingError, {});
        }

        // Last resort, and it must not be able to throw: this catch is the
        // only thing standing between one bad file and a rejected
        // Promise.all that takes the entire batch (and the customer's whole
        // selection) down with it. With the breaker open it is the cheap
        // placeholder, never the full original decoded in the grid.
        return {
          file,
          thumbnail: lastResortThumbnail(
            file,
            thumbnailTimeouts >= THUMBNAIL_BREAKER_AFTER,
            (f) => URL.createObjectURL(f),
            () => placeholderFor(file),
          ),
        };
      }
    };

    await forEachBatch(
      files,
      batchSize,
      async (batch, i) => {
        const batchResults = await Promise.all(
          batch.map((file) => limit(() => thumbnailFor(file)))
        );

        // Awaited so batches are handed over in order. The receiver only adds
        // them to the list here; its byte snapshots run on its own chained
        // queue (order/new handleUpload), so this does not wait on them.
        await uploadCb(
          batchResults.map((result) => result.thumbnail),
          i
        );
      },
      // One batch failing must not cost the customer every photo after it.
      (error, start) => {
        console.error(`Batch at ${start} failed:`, error);
        captureSentryExceptionOnce(error, {
          tags: { area: "photo-upload", stage: "add-batch" },
          extra: { batchStart: start, batchSize, total: files.length },
        });
      },
    );
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: unknown[] = []) => {
      // A new pick replaces the last one's note; onDropRejected runs after this.
      setRejectedCount(0);
      // Counted before filtering: the cap applies to what the picker returned.
      setAndroidLimitHit(
        hitAndroidPickLimit(acceptedFiles.length + rejectedFiles.length),
      );
      setIsUploading(true);
      setDisable(true);
      setIsUploadingLongerThan5Seconds(false);

      timeoutIdRef.current = setTimeout(() => {
        setIsUploadingLongerThan5Seconds(true);
      }, 5000);

      try {
        await processBatch(
          acceptedFiles,
          ADD_BATCH_SIZE,
          async (thumbnails: (string | null)[], curentGlobalIndex: number) => {
            const validThumbnails: string[] = [];
            const validFiles: File[] = [];
            thumbnails.forEach((thumbnail, index) => {
              if (thumbnail !== null) {
                validThumbnails.push(thumbnail);
                validFiles.push(acceptedFiles[index + curentGlobalIndex]);
              }
            });

            await onUpload(validFiles, validThumbnails); // Use filtered arrays
          }
        );
        // onUpload(validFiles, validThumbnails);
      } catch (error) {
        console.error("Batch processing error:", error);
        // Sentry.captureException(error);
        captureSentryExceptionOnce(error, {});
      } finally {
        setIsUploading(false);
        setDisable(false);
        clearTimeout(timeoutIdRef.current);
      }
    },
    [onUpload, setDisable]
  );

  useEffect(() => {
    return () => {
      if (isUploading) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [isUploading]);

  const acceptFileTypes = PHOTO_ACCEPT_TYPES;
  /**
   * Files the accept filter refused.
   *
   * This was previously unread: `useDropzone` returns `fileRejections` and
   * nothing looked at it, so a refused file vanished with no message and no
   * report. It is the only silent failure left in the upload flow — every other
   * path reports — and it is the one that runs FIRST, before add-time
   * snapshotting, thumbnails, HEIC conversion or upload.
   *
   * That ordering is why it matters. Over 14 days those later paths produced 32
   * upload-failure events between them and exactly ONE came from iOS, while
   * iPhone customers were reporting that picking photos does nothing at all.
   * Sentry captures from Mobile Safari elsewhere, so the absence is not the SDK
   * failing to report — it means those customers never reach the instrumented
   * code. This is the gap they were falling into, and now it is on the record:
   * the count of empty `file.type` files is the first thing to read, since iOS
   * and in-app WebViews are exactly what produce them.
   */
  const onDropRejected = useCallback(
    (rejections: DropRejection[]) => {
      if (rejections.length === 0) return;
      const summary = summariseRejections(rejections);

      try {
        Sentry.captureMessage("Picked files rejected by the file picker", {
          level: "warning",
          tags: {
            area: "photo-upload",
            // Filterable: is this the empty-MIME case or a genuinely wrong file?
            allEmptyType: String(summary.emptyTypeCount === summary.total),
          },
          extra: {
            total: summary.total,
            emptyTypeCount: summary.emptyTypeCount,
            codeCounts: summary.codeCounts,
            files: summary.files,
          },
        });
      } catch {
        // Reporting must never be the reason a customer cannot continue.
      }

      // Say it -- inline, not as a pop-up. A customer whose photos silently
      // disappear concludes the site is broken; one who is told how many were
      // refused can convert them or pick different ones. A count, not file
      // names: customers do not recognise "IMG_1009.HEIC".
      setRejectedCount(rejections.length);
    },
    [],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: acceptFileTypes,
  });

  return (
    <>
    <div
      {...getRootProps()}
      style={{
        padding: "20px",
        backgroundColor: mode === "light" ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)",
        color: mode === "light" ? "#333" : "#fff",
        border: mode === "light" ? "3px dashed #222" : "4px dashed #ddd",
        borderRadius: "10px",
        textAlign: "center",
      }}
    >
      <input {...getInputProps()} />
      {isUploading && (
        <>
          <CircularProgress />
          {isUploadingLongerThan5Seconds && (
            <p>{translate("photoUpload.longerUploading")}</p>
          )}
        </>
      )}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100%"
        paddingY={"60px"}
      >
        <AddPhotoAlternateIcon sx={{ fontSize: "50px" }} />
        {isDragActive ? (
          <p>{translate("photoUpload.drop")}</p>
        ) : (
          <p>{translate("photoUpload.dragNDrop")}</p>
        )}
      </Box>
    </div>
    {/*
      * Inline, not an alert(): the customer has done nothing wrong, and the
      * remedy is simply to tap the box above again. Picks made before the
      * upload button append to the same list, so they land in the same item.
      */}
    {rejectedCount > 0 && (
      <Alert
        severity="info"
        sx={{ mt: 1.5 }}
        role="status"
        closeText={translate("order.noteClose")}
        onClose={() => setRejectedCount(0)}
      >
        {translate("photoUpload.rejectedFiles", { count: rejectedCount })}
      </Alert>
    )}
    {androidLimitHit && (
      <Alert severity="info" sx={{ mt: 1.5 }} role="status">
        {translate("photoUpload.androidPickLimit")}
      </Alert>
    )}
    </>
  );
};

export default PhotoUpload;
